import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { baseUrl } from "../../../comman/config";
import Select from "react-select";
import { useNavigate, Link } from "react-router-dom";
import Loader from "../../Loader";
import { menuAboveTable } from "../../../comman/selectMenu";

// `readOnly` is the whole screen in View: every field disabled, and nothing that writes -- no
// Save, no Add Row, no Remove. It is set by the View action on Recipe Master, and also whenever
// the user's operations on that screen stop at View, so that an Edit page reached by URL is not
// an edit page.
export default function Form({ detail, handelIngredientList, duplicate, readOnly = false }) {
  const [formData, setFormData] = useState({
    menuitem_id: "",
    maharaj_id: "",
    // Per recipe, not per ingredient -- see the migration note in
    // sql/receipe_master_std_ras.sql. Read back from the first row of the recipe.
    std_ras: ""
  });
  const [validationErrors, setValidationErrors] = useState({
    menuitem_id: false,
    maharaj_id: false
  });
  const [menuitemList, setMenuitemList] = useState([]);
  const [maharajList, setMaharajList] = useState([]);
  const [ingredientList, setIngredientList] = useState([]);
  const [menuitemCategory, setMenuitemCategory] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [rows, setRows] = useState([
    // std_ras_qty is null -- nobody is typing in that cell. See stdRasQty below.
    { id: 1, ingredients_id: "", qty: "", std_ras_qty: null, ingredients_unit: "", is_main: false },
  ]);
  // Ids in the order the table draws them; see compareRows.
  const [rowOrder, setRowOrder] = useState([]);
  const [processRows, setProcessRows] = useState([
    { id: 1, process: "" },
  ]);
  const [rowCount, setRowCount] = useState(1);
  const [processRowCount, setProcessRowCount] = useState(1);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const navigate = useNavigate();

  // Computed totals
  const totalQty = rows.reduce((sum, r) => sum + parseFloat(r.qty || 0), 0);
  const mainTotal = rows
    .filter(r => r.is_main)
    .reduce((sum, r) => sum + parseFloat(r.qty || 0), 0);

  // STD RAS is the recipe's own figure, entered by the recipe team, and the two columns below
  // start out computed from it:
  //   Std. RAS QTY = STD RAS * (that ingredient's Qty per 100 kgs / 100)
  //   % weightage  = that ingredient's Std. RAS QTY / STD RAS
  //
  // Both read blank until a STD RAS has been entered. 0.000 would look like a measured figure;
  // blank says the recipe has not been given one yet.
  const stdRas = parseFloat(formData.std_ras);
  const hasStdRas = !isNaN(stdRas) && String(formData.std_ras ?? '').trim() !== '';
  // Std. RAS QTY is not a second figure. It and Qty / 100 kgs are one quantity seen two ways,
  // tied by STD RAS and exactly invertible:
  //
  //     Std. RAS QTY    = STD RAS * (Qty per 100 kgs / 100)
  //     Qty per 100 kgs = 100 * Std. RAS QTY / STD RAS
  //
  // So the column reads on every render exactly as it reads on load -- computed from the row's
  // qty -- and typing in it writes the qty that figure implies back into the column beside it,
  // carrying % weightage and the totals with it. Whichever of the two is typed in, the other
  // follows, on one row or on twenty.
  //
  // Nothing extra is stored: receipe_master holds std_ras and qty, and a reopened recipe computes
  // this column again from those.
  //
  // `std_ras_qty` on a row holds only what is being typed in the cell right now, so a
  // half-finished figure is left alone while the caret is in it: a "2" on its way to "20" is not
  // answered with a recomputed 1.000, and a "4." does not lose its point to parseFloat. It is
  // dropped on blur and the cell goes back to reading the formula. null means nobody is typing.
  const stdRasQty = (row) => {
    if (row?.std_ras_qty !== null && row?.std_ras_qty !== undefined) return row.std_ras_qty;
    const q = parseFloat(row?.qty);
    if (!hasStdRas || isNaN(q)) return '';
    return (stdRas * (q / 100)).toFixed(3);
  };
  // Shown for main items only, which is where the sheet puts it -- it is the share of the
  // recipe each main ingredient carries, and the other items are not part of that split.
  // Reads the entered figure when there is one, so a hand edit moves the weightage with it.
  const weightage = (row) => {
    const v = stdRasQty(row);
    if (v === '' || !stdRas) return '';
    return `${((parseFloat(v) / stdRas) * 100).toFixed(2)}%`;
  };

  // Typing a Std. RAS QTY sets the Qty / 100 kgs it implies -- the corresponding change the sheet
  // asks for -- while the keystrokes themselves stay in the cell until it is left. Needs a STD RAS
  // to divide by: without one the two columns are not related to each other and an edit has
  // nothing to write, which is why the cell is disabled until the recipe has one.
  const handleStdRasQtyChange = (id, value) => {
    if (readOnly || !hasStdRas || !stdRas) return;
    const v = parseFloat(value);
    // Rounded to the three decimals this column is read in, and back through Number so the qty
    // reads 81.25 rather than 81.25000000000001. An emptied cell empties the qty with it: the two
    // are the one quantity, so there is no figure left for the other column to show.
    const qty =
      String(value).trim() === '' || isNaN(v) ? '' : String(Number(((v * 100) / stdRas).toFixed(3)));
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, std_ras_qty: value, qty } : r)));
  };

  // Leaving the cell hands it back to the formula, so what stands in it is the figure the row's
  // qty gives -- the same figure the column would show on a reload.
  const handleStdRasQtyBlur = (id) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, std_ras_qty: null } : r)));
  };


  // Water leads every recipe, whichever maharaj the recipe is for: it is the ingredient a new
  // recipe opens on, and it sorts to the top of the table on the recipes that already carry it.
  //
  // Found by name rather than by a fixed id, so this holds on any database the app is pointed at
  // instead of on one row of one copy of ingredients_master.
  const waterIngredient = ingredientList.find(
    (i) => String(i.eng_name || "").trim().toLowerCase() === "water"
  );
  const isWaterRow = (row) =>
    !!waterIngredient && row.ingredients_id == waterIngredient.id;

  // Display order: water first, then main items, then the biggest quantities. It is worked out
  // when the set of rows changes -- a row added or removed, an ingredient chosen, a main item
  // ticked, a recipe loaded -- and held in state until then, rather than re-run on every render.
  //
  // Re-sorting as the table is typed in is what made these columns look like they worked on one
  // recipe and not on the next. Both quantity columns move qty, qty decides the order, and a row
  // that changes places is lifted out from under the caret; the browser answers a moved input by
  // dropping focus, so the rest of the number goes nowhere. The row only changes places when the
  // figure being typed crosses its neighbour's, which is why it came and went.
  const compareRows = (a, b) => {
    if (isWaterRow(a) !== isWaterRow(b)) return isWaterRow(a) ? -1 : 1;
    if (!!a.is_main !== !!b.is_main) return a.is_main ? -1 : 1;
    return (parseFloat(b.qty) || 0) - (parseFloat(a.qty) || 0);
  };
  const resort = (list) => setRowOrder([...list].sort(compareRows).map((r) => r.id));
  // Keyed on the table's shape -- which rows there are, what each one is, which are main -- and
  // deliberately not on qty, which is what both quantity columns write.
  const orderSignature = rows
    .map((r) => `${r.id}:${r.ingredients_id}:${r.is_main ? 1 : 0}`)
    .join('|');
  useEffect(() => {
    resort(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature, waterIngredient?.id]);
  // A row the order has not been told about yet goes last, until the effect above places it.
  const orderIndex = (row) => {
    const i = rowOrder.indexOf(row.id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const displayRows = rowOrder.length
    ? [...rows].sort((a, b) => orderIndex(a) - orderIndex(b))
    : [...rows].sort(compareRows);

  useEffect(() => {
    getMenuitemList();
    getMaharajList();
    getIngredientList();
    getMenuitemCategory();
    getUnitList();
  }, []);

  // Only for a recipe being started from nothing -- the one blank row the form opens with. An
  // existing recipe is left exactly as it was saved: adding an ingredient to it behind the user's
  // back would change a recipe nobody opened to change.
  useEffect(() => {
    if (detail || !waterIngredient) return;
    setRows((prev) => {
      if (prev.length !== 1 || prev[0].ingredients_id !== "") return prev;
      return [{ ...prev[0], ingredients_id: String(waterIngredient.id), ingredients_unit: waterIngredient.unit }];
    });
  }, [detail, waterIngredient]);

  useEffect(() => {
    const callApi = async () => {
      // ingredientList starts as [], which is truthy: this used to load the recipe twice, once
      // with no ingredients known and once with them, and the two runs raced. Whichever landed
      // last won -- so the table's order, and for a moment its STD RAS, depended on which fetch
      // came back first.
      if (detail && ingredientList.length) {
        // Merged, not replaced: std_ras is set further down this same function, after an await.
        // Replacing the object dropped it in between, and a render caught in that gap blanked
        // and disabled the whole Std. RAS QTY column -- with the STD RAS field left showing a
        // figure the form no longer held.
        setFormData((prev) => ({
          ...prev,
          menuitem_id: detail[0].id || "",
          maharaj_id: detail[0].maharaj?.id || '',
        }));
        const condition = `menu_item_id = ${detail[0].id} AND maharaj = ${detail[0].maharaj?.id}`;
        const ingResponse = await axios.get(
          `${baseUrl}/comman/list?table=receipe_master&condition=${condition}`
        );
        // Same value on every row of the recipe; the first one is as good as any.
        const savedStdRas = ingResponse.data.data[0]?.std_ras;
        if (savedStdRas !== undefined && savedStdRas !== null) {
          setFormData((prev) => ({ ...prev, std_ras: savedStdRas }));
        }
        const newArray = ingResponse.data.data.map((item, index) => {
          const unit =
            ingredientList.find((v) => v.id == item.ingredient_id)?.unit ?? null;
          return {
            id: index + 1,
            ingredients_id: item.ingredient_id.toString(),
            qty: item.qty,
            // Nothing is read back for the Std. RAS QTY cell: it is not stored, so a saved
            // recipe opens with the whole column computed from STD RAS and qty.
            std_ras_qty: null,
            ingredients_unit: unit,
            is_main: item.is_main ? true : false,
          };
        });

        const response = await axios.get(
          `${baseUrl}/comman/list?table=receipe_master_process&condition=${condition}`
        );
        setRows(newArray);
        setRowCount(newArray.length);
        setProcessRows(response.data.data);
        setProcessRowCount(response.data.data.length);
      }
    };
    callApi();
  }, [detail, ingredientList]);

  const getMenuitemList = async () => {
    const response = await axios.get(`${baseUrl}/item/list`);
    const list = response.data.data.filter((item) => item.status == 1);
    setMenuitemList(list);
  };

  const getMaharajList = async () => {
    const response = await axios.get(`${baseUrl}/maharaj/list`);
    const filteredList = response.data.data.filter((maharaj) => maharaj.status === 1);
    setMaharajList(filteredList);
  };

  const getIngredientList = async () => {
    setLoading(true);
    const response = await axios.get(`${baseUrl}/ingredients/list?limit=all`);
    const list = response.data.data.filter((item) => item.status == 1);
    setIngredientList(list);
    if (detail) {
      handelIngredientList(list);
    }
    setLoading(false);
  };

  const getMenuitemCategory = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=item_category_master&condition=status=1`
    );
    setMenuitemCategory(response.data.data);
  };

  const getUnitList = async () => {
    const response = await axios.get(`${baseUrl}/comman/list?table=units`);
    setUnitList(response.data.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // The button is not drawn in View, so this is only reached by a stray Enter key in a field
    // the browser left focusable. It saves nothing either way.
    if (readOnly) return;
    const newValidationErrors = {
      menuitem_id: formData.menuitem_id === "",
      maharaj_id: formData.maharaj_id === "",
      rows: isAddRowDisabled(),
    };
    setValidationErrors(newValidationErrors);

    // The main ingredients are what the recipe is scaled from, so together they have to come to
    // 100 kgs. Off that, the Std. RAS QTY column no longer divides the entered STD RAS between
    // them and every figure derived from it is wrong -- so this stops the save rather than only
    // warning, which is what the message asks for ("please make a change").
    if (hasStdRas && rows.some((r) => r.is_main)) {
      const main = Number(mainTotal.toFixed(3));
      if (main > 100) {
        alert('Std. RAS QTY નક્કી કરેલ RAS કરતા વધુ છે, તેથી કૃપા કરીને ફેરફાર કરો');
        return;
      }
      if (main < 100) {
        alert('Std. RAS QTY, નક્કી કરેલ RAS કરતા ઓછી છે, તેથી કૃપા કરીને ફેરફાર કરો.');
        return;
      }
    }

    const hasErrors = Object.values(newValidationErrors).some((error) => error);
    if (hasErrors) {
      const firstInvalidInput = formRef.current.querySelector(".invalid");
      if (firstInvalidInput) {
        firstInvalidInput.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      let url;
      try {
        setPopupOpen(true);
        if (detail && !duplicate) {
          url = `receipe/edit/${detail[0].id}`;
        } else {
          url = "receipe/add";
        }
        const response = await axios.post(`${baseUrl}/${url}`, {
          formData: formData,
          rows: rows,
          processRows: processRows
        });
        setPopupOpen(false);
        if (response.data.status == true) {
          if (detail && !duplicate) {
            window.location.reload();
          } else {
            navigate("/receipe/list");
          }
        } else {
          setValidationErrors({ error: response.data.msg });
        }
      } catch (error) {
        setPopupOpen(false);
        console.log(error);
      }
    }
  };

  const handleMenuitemChange = (value) => {
    setFormData((prevData) => ({ ...prevData, menuitem_id: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    // Every Std. RAS QTY in the table is computed from STD RAS, so a new one re-reads the whole
    // column -- including a cell somebody had left mid-edit.
    if (name === 'std_ras') {
      setRows((prev) => prev.map((r) => (r.std_ras_qty === null ? r : { ...r, std_ras_qty: null })));
    }
  };

  const handleAddRow = (e) => {
    e.preventDefault();
    setRowCount(rowCount + 1);
    setRows([
      ...rows,
      { id: rowCount + 1, ingredients_id: "", qty: "", std_ras_qty: null, ingredients_unit: "", is_main: false },
    ]);
  };

  const handleProcessAddRow = (e) => {
    e.preventDefault();
    setProcessRowCount(processRowCount + 1);
    setProcessRows([
      ...processRows,
      { id: processRowCount + 1, process: "" },
    ]);
  };

  const handleRemoveRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleProcessRemoveRow = (id) => {
    setProcessRows(processRows.filter((row) => row.id !== id));
  };

  const handleIngredientsChange = (id, value) => {
    // A duplicate is refused, and the cell it was picked in is emptied so the next choice can go
    // straight into it. The refusal used to be only half a refusal: the row kept nothing, but the
    // dropdown went on displaying the ingredient just picked, so the screen showed an ingredient
    // that the save would not write. (react-select was handed `undefined` for a row with no
    // ingredient, which leaves it uncontrolled and free to keep its own selection -- hence the
    // `?? null` on its value below.)
    //
    // The row doing the picking is excluded from the check: re-selecting what a row already holds
    // is not a duplicate of anything.
    const isDuplicate = rows.some(
      (row) => row.id !== id && row.ingredients_id == value
    );
    if (isDuplicate) {
      alert("Selected ingredient already exist!");
      setRows(
        rows.map((row) =>
          row.id === id ? { ...row, ingredients_id: "", ingredients_unit: "" } : row
        )
      );
      return;
    }
    const unit = ingredientList.find((item) => item.id == value)?.unit ?? "";
    setRows(
      rows.map((row) =>
        row.id === id
          ? { ...row, ingredients_id: value.toString(), ingredients_unit: unit }
          : row
      )
    );
  };

  // The other half of the pair: a qty edit moves Std. RAS QTY, by dropping anything half-typed in
  // that cell and letting the formula read the new qty.
  const handleQtyChange = (id, value) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, qty: value, std_ras_qty: null } : row)));
  };

  const handleIsMainChange = (id, checked) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, is_main: checked } : row)));
  };

  const handleProcessChange = (id, value) => {
    setProcessRows(processRows.map((row) => (row.id === id ? { ...row, process: value } : row)));
  };

  const handleIngredientsUnitChange = (id, value) => {
    setRows(
      rows.map((row) =>
        row.id === id ? { ...row, ingredients_unit: value } : row
      )
    );
  };

  const isRowValid = (row) => {
    if (row.ingredients_id.trim() !== "" && row.qty.trim() !== "") {
      const qtyRegex = /^[0-9]+(?:\.[0-9]+)?$/;
      return qtyRegex.test(row.qty.trim());
    }
    return false;
  };

  const isAddRowDisabled = () => {
    return rows.some((row) => !isRowValid(row));
  };

  const scrollToProcess = (e) => {
    e.preventDefault();
    const element = document.querySelector('.production_process');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };


  return (
    <>
      {loading && <Loader />}
      <div>
        <div>
          <form ref={formRef}>
            <div className="space-y-12">
              <div>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-x-5 gap-y-8">
                  <div className="md:col-span-2 ms-4">
                    <label htmlFor="menuitem_id" className="block text-sm font-medium leading-6 text-gray-900">
                      Item Name
                    </label>
                    <div className="mt-2">
                      <Select
                        {...menuAboveTable}
                        classNamePrefix="select"
                        value={
                          menuitemList
                            .filter((item) => item.id === formData.menuitem_id)
                            .map((item) => ({
                              value: item.id,
                              label: `${item.item_name} (${item.eng_name})`,
                              eng_name: item.eng_name,
                            }))[0]
                        }
                        name="menuitem_id"
                        onChange={(selectedOption) => {
                          handleMenuitemChange(selectedOption.value);
                        }}
                        options={menuitemList.map((item) => ({
                          value: item.id,
                          label: `${item.item_name} (${item.eng_name})`,
                          eng_name: item.eng_name,
                          category: item.category,
                        }))}
                        isDisabled={readOnly || (detail && detail.length > 0 && !duplicate)}
                        filterOption={(option, inputValue) => {
                          return (
                            option.data.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                            option.data.eng_name.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                      />
                    </div>
                    {validationErrors.menuitem_id && (
                      <div className="text-red-500">Please select a menuitem.</div>
                    )}
                  </div>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                  <div className="md:col-span-2 ms-4">
                    <label htmlFor="maharaj_id" className="block text-sm font-medium leading-6 text-gray-900">Maharaj Name</label>
                    <div className="mt-2">
                      <select
                        id="maharaj_id"
                        name="maharaj_id"
                        type="text"
                        value={formData.maharaj_id}
                        onChange={handleInputChange}
                        disabled={readOnly || (detail && detail.length > 0 && !duplicate)}
                        className={`block w-full rounded-md border ${validationErrors.maharaj_id ? 'border-red-500' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}>
                        <option value="">Select Maharaj</option>
                        {maharajList.map((item) => (
                          <option value={item.id} key={item.id}>{item.maharaj_name}</option>
                        ))};
                      </select>
                    </div>
                    {validationErrors.maharaj_id && <div className="text-red-500">Please enter a maharaj name.</div>}
                  </div>
                  {/* Entered by the recipe team. The Std. RAS QTY and % weightage columns below
                      are computed from it, so both stay blank until it has a value. */}
                  <div className="md:col-span-2 ms-4">
                    <label htmlFor="std_ras" className="block text-sm font-medium leading-6 text-gray-900">STD RAS</label>
                    <div className="mt-2">
                      <input
                        id="std_ras"
                        name="std_ras"
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="STD RAS"
                        value={formData.std_ras}
                        onChange={handleInputChange}
                        disabled={readOnly}
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                        className="block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 bg-yellow-50"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6">
                  <Link to="/receipe/list">
                    <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                      {readOnly ? 'Back' : 'Cancel'}
                    </button>
                  </Link>
                  <button onClick={scrollToProcess} className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2">Production Process</button>
                  {!readOnly && <button
                    onClick={handleSubmit}
                    type="submit"
                    className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Save
                  </button>}
                </div>

                {/* Totals above table */}
                <div className="mt-3 space-y-1">
                  <div>
                    <span className={`text-sm font-bold ${
                      mainTotal > 100 ? 'text-red-600' :
                      mainTotal < 95 ? 'text-orange-500' :
                      'text-gray-900'
                    }`}>Main Ingredients Total : </span>
                    <span className={`text-sm ${
                      mainTotal > 100 ? 'text-red-600' :
                      mainTotal < 95 ? 'text-orange-500' :
                      'text-gray-900'
                    }`}>{mainTotal.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-sm font-bold text-gray-900">All Ingredients Total : </span>
                    <span className="text-sm text-gray-900">{totalQty.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-5">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr className="text-center">
                        <th className="px-4 py-2">Ingredients</th>
                        <th className="px-4 py-2">Unit</th>
                        <th className="px-4 py-2">Qty / 100 kgs</th>
                        <th className="px-4 py-2">Std. RAS QTY<br /><span className="font-normal text-xs">(100 persons)</span></th>
                        <th className="px-4 py-2">% weightage</th>
                        <th className="px-4 py-2">Main Item</th>
                        <th className="px-4 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayRows.map((row) => (
                        <tr key={row.id} className={row.is_main ? 'bg-blue-50' : ''}>
                          <td className="px-4 py-2 text-center">
                            <Select
                              {...menuAboveTable}
                              classNamePrefix="select"
                              className="w-80"
                              value={
                                ingredientList
                                  .filter((item) => item.id == row.ingredients_id)
                                  .map((item) => ({
                                    value: item.id,
                                    label: `${item.ingredient} (${item.eng_name})`,
                                    eng_name: item.eng_name,
                                  }))[0] ?? null
                              }
                              onChange={(selectedOption) => {
                                handleIngredientsChange(row.id, selectedOption.value);
                              }}
                              isDisabled={readOnly}
                              options={ingredientList.map((item) => ({
                                value: item.id,
                                label: `${item.ingredient} (${item.eng_name})`,
                                eng_name: item.eng_name,
                              }))}
                              filterOption={(option, inputValue) => {
                                return (
                                  option.data.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                  option.data.eng_name.toLowerCase().includes(inputValue.toLowerCase())
                                );
                              }}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <input
                              type="text"
                              value={row.ingredients_unit?.name}
                              disabled
                              className="w-20 rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <input
                              type="number"
                              value={row.qty}
                              onChange={(e) => handleQtyChange(row.id, e.target.value)}
                              disabled={readOnly}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                              className="w-20 rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                            />
                          </td>
                          {/* Typed in freely; the qty beside it follows, and % weightage -- which
                              stays read-only -- follows both. Disabled until the recipe has a STD
                              RAS, since that is what ties this cell to the qty. */}
                          <td className="px-4 py-2 text-center">
                            <input
                              type="number"
                              step="0.001"
                              value={stdRasQty(row)}
                              onChange={(e) => handleStdRasQtyChange(row.id, e.target.value)}
                              onBlur={() => handleStdRasQtyBlur(row.id)}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                              disabled={readOnly || !hasStdRas}
                              placeholder={hasStdRas ? '' : 'STD RAS first'}
                              className="w-24 rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 bg-yellow-50"
                            />
                          </td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">{row.is_main ? weightage(row) : ''}</td>
                          <td className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={row.is_main}
                              onChange={(e) => handleIsMainChange(row.id, e.target.checked)}
                              disabled={readOnly}
                              className="w-4 h-4 accent-blue-500 cursor-pointer disabled:cursor-not-allowed"
                              title="Mark as main ingredient"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            {!readOnly && row.id !== 1 && (
                              <button
                                onClick={() => handleRemoveRow(row.id)}
                                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {validationErrors.rows && (
                    <div className="text-red-500">
                      Please add at least one ingredient and their quantity.
                    </div>
                  )}
                </div>
              </div>

              {validationErrors.error && (
                <div className="text-red-500">
                  Please check receipe already exist for this menu item.
                </div>
              )}
            </div>

            {!readOnly && <div className="mt-6 flex items-center justify-end gap-x-6">
              <button
                onClick={handleAddRow}
                disabled={isAddRowDisabled()}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 text-sm rounded"
              >
                Add Row
              </button>
            </div>}
          </form>
        </div>

      </div>

      {/* Production process section */}
      <div className="production_process mt-8">
        <h3 className="text-lg sm:text-l md:text-l lg:text-l font-bold">Standard Production Process</h3>
        <div className="mt-5">
          <table className="divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              {processRows.map((row, index) => (
                <tr key={row.id}>
                  <td className="px-4 py-2 text-center">Step {index + 1}</td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="text"
                      value={row.process}
                      onChange={(e) => handleProcessChange(row.id, e.target.value)}
                      disabled={readOnly}
                      placeholder="Process Step"
                      className="w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    {!readOnly && row.id !== 1 && (
                      <button
                        onClick={() => handleProcessRemoveRow(row.id)}
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!readOnly && <div className="mt-6 flex items-center justify-end gap-x-6">
          <button
            onClick={handleProcessAddRow}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 text-sm rounded"
          >
            Add Process Row
          </button>
        </div>}
      </div>
    </>
  );
}
